import { useCallback, useState } from 'react'

// ── Types ──────────────────────────────────────────────────────────────────────

/** Backend-sync state of a single scan-log entry. */
export type ScanSyncStatus = 'syncing' | 'synced' | 'error'

/** Outcome of a single scan, mirroring the on-screen result shown to staff. */
export type ScanLogOutcome = 'approved' | 'already_attended' | 'rejected'

/** One row in the scanned-participants list. */
export interface ScanLogEntry {
  /** Unique per scan event (not the participant id — the same participant may be scanned twice). */
  id: string
  participantId: string
  firstName: string
  lastName: string
  email: string
  role?: 'AUDIENCE' | 'SPEAKER' | 'STAFF' | 'HOST'
  outcome: ScanLogOutcome
  syncStatus: ScanSyncStatus
  scannedAt: number
}

// ── sessionStorage helpers ─────────────────────────────────────────────────────

function storageKey(eventId: string): string {
  return `proaxiv.checkin.scanLog.${eventId}`
}

function readStoredEntries(eventId: string): ScanLogEntry[] {
  try {
    const raw = sessionStorage.getItem(storageKey(eventId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as ScanLogEntry[]) : []
  } catch /* v8 ignore next */ {
    return []
  }
}

function writeStoredEntries(eventId: string, entries: ScanLogEntry[]): void {
  try {
    sessionStorage.setItem(storageKey(eventId), JSON.stringify(entries))
  } catch /* v8 ignore next */ {
    // ignore storage errors (e.g. private browsing / quota exceeded)
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseScanLogReturn {
  /** Newest scan first — matches the "last ones at top" display requirement. */
  entries: ScanLogEntry[]
  /** Prepends a new scan-log entry and persists it to `sessionStorage`. */
  addEntry: (entry: ScanLogEntry) => void
  /** Patches the sync status of an existing entry by id, and persists it. */
  updateStatus: (id: string, syncStatus: ScanSyncStatus) => void
}

/**
 * Session-scoped (per-tab) list of scanned participants for the check-in kiosk.
 *
 * Backed by `sessionStorage` so the list survives a page refresh but is cleared
 * as soon as the tab is closed — matching the "lose the list if I close the tab"
 * requirement without needing any explicit reset logic.
 */
export function useScanLog(eventId: string): UseScanLogReturn {
  const [entries, setEntries] = useState<ScanLogEntry[]>(() => readStoredEntries(eventId))

  const addEntry = useCallback(
    (entry: ScanLogEntry) => {
      setEntries((prev) => {
        const next = [entry, ...prev]
        writeStoredEntries(eventId, next)
        return next
      })
    },
    [eventId]
  )

  const updateStatus = useCallback(
    (id: string, syncStatus: ScanSyncStatus) => {
      setEntries((prev) => {
        const next = prev.map((e) => (e.id === id ? { ...e, syncStatus } : e))
        writeStoredEntries(eventId, next)
        return next
      })
    },
    [eventId]
  )

  return { entries, addEntry, updateStatus }
}
